import type { Form, FormKind, FormSystemField, ProjectFieldBinding } from '@servicetim/shared';
import { useFieldBindings, usePriorities, useResolvedForm } from '@servicetim/ui/api';
import {
  type SchemaFormOption,
  type SchemaFormSection,
  type SchemaFormValues,
  sectionsFromBindings,
  sectionsFromResolvedForm,
} from '@servicetim/ui/components';
import { useMemo } from 'react';
import { useProjectFormsIfAllowed, useSelectOptions } from './api';

/** System fields the create dialog offers when no form exists (same order as the form kinds). */
export const CREATE_SYSTEM_FIELDS: readonly FormSystemField[] = [
  'summary',
  'description',
  'priority_id',
  'assignee_id',
  'labels',
  'due_at',
];

/** The form the API would pick: type-specific first, then the project-wide one. */
export function pickForm(
  forms: readonly Form[] | undefined,
  issueTypeId: string | undefined,
): Form | undefined {
  if (!forms) return undefined;
  return (
    forms.find((f) => issueTypeId !== undefined && f.issueTypeId === issueTypeId) ??
    forms.find((f) => f.issueTypeId === null)
  );
}

/** Binding `defaultValue`s applicable to the issue type, keyed by field key (create dialogs). */
export function bindingDefaults(
  bindings: readonly ProjectFieldBinding[] | undefined,
  issueTypeId: string | undefined,
): SchemaFormValues {
  const out: SchemaFormValues = {};
  for (const b of bindings ?? []) {
    const applies =
      b.issueTypeIds === null || issueTypeId === undefined || b.issueTypeIds.includes(issueTypeId);
    if (applies && b.defaultValue !== undefined && b.defaultValue !== null) {
      out[b.fieldKey] = b.defaultValue;
    }
  }
  return out;
}

/** Removes fields rendered elsewhere (header, right rail) and drops sections left empty. */
export function withoutFields(
  sections: readonly SchemaFormSection[],
  keys: readonly string[],
): SchemaFormSection[] {
  const drop = new Set(keys);
  return sections
    .map((s) => ({ ...s, fields: s.fields.filter((f) => !drop.has(f.key)) }))
    .filter((s) => s.fields.length > 0);
}

/** Marks system fields read-only (transition forms accept only `cf_` values). */
export function systemFieldsReadOnly(sections: readonly SchemaFormSection[]): SchemaFormSection[] {
  return sections.map((s) => ({
    ...s,
    fields: s.fields.map((f) => (f.system ? { ...f, readOnly: true } : f)),
  }));
}

export interface TicketFormSource {
  sections: SchemaFormSection[] | undefined;
  isPending: boolean;
  error: unknown;
  /** Where the sections came from; null while unknown. */
  source: 'form' | 'bindings' | null;
  /** Binding defaults (bindings source only); `{}` otherwise. */
  defaults: SchemaFormValues;
  priorities: SchemaFormOption[] | undefined;
}

export interface TicketFormSourceArgs {
  projectId: string | undefined;
  issueTypeId: string | undefined;
  kind: FormKind;
  /** Whether the caller may list the project's forms (`project.canAdmin`). */
  canAdmin: boolean;
  /** System fields to prepend when falling back to bindings. */
  systemFields?: readonly FormSystemField[];
  enabled?: boolean;
}

/**
 * Sections for a ticket form: the resolved form of the matching kind when the caller can list
 * forms (project admins), otherwise the project's bindings for the issue type plus the given
 * system fields. Select options come with the resolved form or are loaded per field.
 */
export function useTicketFormSource(args: TicketFormSourceArgs): TicketFormSource {
  const { projectId, issueTypeId, kind, canAdmin, systemFields = [], enabled = true } = args;
  const priorities = usePriorities(enabled ? projectId : undefined);
  const priorityOptions = useMemo<SchemaFormOption[] | undefined>(
    () => priorities.data?.map((p) => ({ id: p.id, label: p.name, color: p.color })),
    [priorities.data],
  );

  const forms = useProjectFormsIfAllowed(projectId, kind, enabled && canAdmin);
  const form = pickForm(forms.data, issueTypeId);
  const resolved = useResolvedForm(projectId, form?.id);

  const fallback = enabled && (!canAdmin || forms.isError || (forms.isSuccess && !form));
  const bindings = useFieldBindings(fallback ? projectId : undefined);
  const options = useSelectOptions(fallback ? bindings.data : undefined);

  return useMemo<TicketFormSource>(() => {
    if (!enabled || projectId === undefined) {
      return {
        sections: undefined,
        isPending: false,
        error: null,
        source: null,
        defaults: {},
        priorities: priorityOptions,
      };
    }
    if (resolved.data && form) {
      return {
        sections: sectionsFromResolvedForm(resolved.data, form.layout, {
          priorities: priorityOptions,
        }),
        isPending: false,
        error: null,
        source: 'form',
        defaults: {},
        priorities: priorityOptions,
      };
    }
    if (fallback && bindings.data && !options.isPending) {
      return {
        sections: sectionsFromBindings(bindings.data, {
          issueTypeId,
          optionsByFieldId: options.optionsByFieldId,
          systemFields,
          priorities: priorityOptions,
          systemTitle: systemFields.length > 0 ? 'Details' : undefined,
          customTitle: systemFields.length > 0 ? 'Fields' : undefined,
        }),
        isPending: false,
        error: null,
        source: 'bindings',
        defaults: bindingDefaults(bindings.data, issueTypeId),
        priorities: priorityOptions,
      };
    }
    const error = resolved.error ?? bindings.error ?? null;
    return {
      sections: undefined,
      isPending: error === null,
      error,
      source: null,
      defaults: {},
      priorities: priorityOptions,
    };
  }, [
    enabled,
    projectId,
    issueTypeId,
    form,
    resolved.data,
    resolved.error,
    fallback,
    bindings.data,
    bindings.error,
    options,
    systemFields,
    priorityOptions,
  ]);
}
